import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtDT } from "@/lib/timefmt";
import { toast } from "sonner";
import { SortTh, FilterTh, filterRows, sortRows } from "@/components/tableTools";

export default function UserLogTab() {
  const { user } = useAuth();
  const isSuper = (user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [members, setMembers] = useState([]);
  const [sort, setSort] = useState({ key: "user", dir: "asc" });
  const [qf, setQf] = useState({ user: "", company: "", org: "", role: "", login: "", entry: "" });

  const userText = (m) => ({
    user: `${m.first_name || ""} ${m.last_name || ""} ${m.email || ""} ${m.external_user_id || ""}`,
    company: m.company_name || "",
    org: [m.unit_name, m.subunit_name, m.team_name].filter(Boolean).join(" / ") || "Unassigned",
    role: `${m.role_name || ""} ${m.admin_level ? `${m.admin_level} admin` : ""}`,
    login: fmtDT(m.last_login_at),
    entry: fmtDate(m.last_entry_date),
  });
  const userKey = (m, k) => (k === "login" ? m.last_login_at || "" : k === "entry" ? m.last_entry_date || "" : userText(m)[k] || "");
  const shown = sortRows(filterRows(members, userText, qf), sort, userKey);

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (isSuper ? r.data[0]?.id : user?.company_id) || "");
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/admin/user-log?company_id=${companyId}`).then((r) => setMembers(r.data.members))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-5">
      {isSuper && companies.length > 1 && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 max-w-sm">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Company</label>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="userlog-company-select"
            className="w-full px-3 py-2.5 rounded-md bg-slate-950 border border-slate-800 text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}
      <div className="rounded-lg border border-slate-800 bg-slate-900/60 overflow-hidden" data-testid="user-log-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <SortTh label="User" k="user" sort={sort} setSort={setSort} />
                <SortTh label="Company" k="company" sort={sort} setSort={setSort} />
                <SortTh label="Org Placement" k="org" sort={sort} setSort={setSort} />
                <SortTh label="Role" k="role" sort={sort} setSort={setSort} />
                <SortTh label="Last Login" k="login" sort={sort} setSort={setSort} />
                <SortTh label="Last Entry" k="entry" sort={sort} setSort={setSort} />
              </tr>
              <tr className="border-b border-slate-800 text-left">
                <FilterTh value={qf.user} onChange={(v) => setQf((f) => ({ ...f, user: v }))} testId="userlog-qfilter-user" />
                <FilterTh value={qf.company} onChange={(v) => setQf((f) => ({ ...f, company: v }))} testId="userlog-qfilter-company" />
                <FilterTh value={qf.org} onChange={(v) => setQf((f) => ({ ...f, org: v }))} testId="userlog-qfilter-org" />
                <FilterTh value={qf.role} onChange={(v) => setQf((f) => ({ ...f, role: v }))} testId="userlog-qfilter-role" />
                <FilterTh value={qf.login} onChange={(v) => setQf((f) => ({ ...f, login: v }))} testId="userlog-qfilter-login" />
                <FilterTh value={qf.entry} onChange={(v) => setQf((f) => ({ ...f, entry: v }))} testId="userlog-qfilter-entry" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {shown.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">No users found.</td></tr>
              )}
              {shown.map((m) => (
                <tr key={m.id} className="hover:bg-slate-900/60" data-testid={`userlog-row-${m.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{m.first_name} {m.last_name}</div>
                    <div className="text-xs text-slate-500">{m.email}</div>
                    <div className="text-[10px] text-slate-600 font-mono-num">{m.external_user_id}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{m.company_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-300">
                    {[m.unit_name, m.subunit_name, m.team_name].filter(Boolean).join(" / ") || "Unassigned"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-300">
                    {m.role_name || "—"}
                    {m.admin_level && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-600/20 text-blue-300 border border-blue-600/40 text-[9px] uppercase tracking-widest font-mono-num">
                        {m.admin_level} admin
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-300">{fmtDT(m.last_login_at)}</td>
                  <td className="px-4 py-3 font-mono-num text-xs text-slate-300">{fmtDate(m.last_entry_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
